// strona koszyka - lokalny override
// @ts-nocheck

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Trans, useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { CreditCard, ChevronDown } from "react-bootstrap-icons";
import classnames from "classnames";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import { useAppNavigate, useNotifications, useRWD } from "hooks";
import { useSelector } from "store";
import {
  useGetCart,
  useGetCartsAll,
  useGetCartMainData,
  usePutCartMainData,
  useGetCartPaymentsExpired,
  usePostVerifyCheckoutStep,
  useGetCheckoutAgreement,
} from "api";
import { Alert, Container, CheckoutSteps, Link } from "components/controls";
import { CartPositions, CartSummary } from "components/containers";
import { ActionBar } from "pages/Cart/components";
import { Contracts } from "pages/Checkout/components";

import styles from "theme/pages/Cart/Cart.module.scss";
import { ArrowIcon } from "assets/icons";

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const queryClient = useQueryClient();
  const { showErrorMessage } = useNotifications();
  const { isMobile } = useRWD();
  const { currentCartId } = useSelector((state) => state.cart);

  // ID aktualnego koszyka z url'a
  const { id } = useParams();
  // przekształcenie w numer
  const cartId = useMemo(() => parseInt(id || ""), [id]);

  // fraza wyszukiwania
  const [searchQuery, setSearchQuery] = useState("");

  // lista rozwiniętych sekcji
  const [expandedSections, setExpandedSections] = useState(["contracts", "comment"]);

  // komentarz do zamówienia
  const [comment, setComment] = useState("");

  // wybrane zgody
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);

  // pobranie informacji o przedawnionych płatności - potrzebne do wyświetlenia alertu
  const { data: paymentsExpiredData } = useGetCartPaymentsExpired(cartId);

  // pobranie zgód
  const { data: checkoutAgreementData, refetch: refetchCheckoutAgreementData } =
    useGetCheckoutAgreement({
      page: 1,
      limit: 999,
      cartId: cartId,
    });

  // pobranie listy koszyków
  const { data: cartsData, refetch: refetchCartsData } = useGetCartsAll({
    enabled: false,
    onSuccess: (data) => {
      const currentCartItem = data.items.find((item) => item.id === cartId);
      const firstCartId = data?.items[0]?.id;
      const secondCartId = data?.items[0]?.id;
      currentCartItem
        ? refetchCartData()
        : navigate(
            firstCartId && firstCartId !== cartId
              ? `/cart/${firstCartId}`
              : secondCartId && secondCartId !== cartId
              ? `/cart/${secondCartId}`
              : "/"
          );
    },
  });

  // walidacja koszyka
  const { mutateAsync: validateCart } = usePostVerifyCheckoutStep(cartId || 0, {
    onSuccess: async () => {
      (currentCart?.products_count || 0) === 0
        ? showErrorMessage(t("Koszyk jest pusty"))
        : navigate(`/checkout/${cartId}`, { state: { isFromCart: true } });
    },
  });

  // odświeżanie szczegółów koszyka
  const { refetch: refetchCartData } = useGetCart(cartId, {
    enabled: false,
  });

  // main data koszyka (m.in. komentarz)
  const { data: cartMainData } = useGetCartMainData(cartId, {
    onSuccess: (data) => {
      if (data?.comment !== undefined) setComment(data.comment || "");
    },
  });

  // aktualizacja main data (zapis komentarza)
  const { mutate: putCartMainData, mutateAsync: putCartMainDataAsync } = usePutCartMainData(cartId);

  useEffect(() => {
    if (currentCartId) {
      navigate(`/cart/${currentCartId}`);
    }
  }, [currentCartId]);

  useEffect(() => {
    setComment(cartMainData?.comment || "");
  }, [cartId, cartMainData?.comment]);

  const currentCart = useMemo(
    () => cartsData?.items.find((item) => item.id === cartId),
    [cartsData, cartId]
  );

  const toggleSections = (key: string) => {
    const isExpanded = expandedSections.includes(key);
    setExpandedSections(
      isExpanded
        ? expandedSections.filter((section) => section !== key)
        : [...expandedSections, key]
    );
  };

  const handleProceedToCheckout = () => {
    const saveCommentThenValidate = async () => {
      if (comment !== (cartMainData?.comment ?? "")) {
        await putCartMainDataAsync({ comment });
        queryClient.invalidateQueries(["cart-main-data", cartId]);
      }
      validateCart({ stepSymbol: "cart" });
    };
    saveCommentThenValidate();
  };

  return (
    <div
      className={classnames(styles.componentWrapper, "StylePath-Pages-Cart")}
    >
      <Helmet>
        <title>Koszyk - Intermal B2B</title>
        {/* INHB-929: canonical bez parametrów zapytania */}
        <link rel="canonical" href={`${window.location.origin}${window.location.pathname}`} />
      </Helmet>
      <Container>
        <div className={styles.header}>
          <Link to="/products" className={styles.link}>
            <ArrowIcon className={styles.backIcon} />{" "}
            <Trans>Wróć do zakupów</Trans>
          </Link>

          <div className={styles.checkoutContainer}>
            <CheckoutSteps
              currentStepIndex={0}
              cartId={cartId}
              disabled={(currentCart?.products_count || 0) === 0}
            />
          </div>
        </div>

        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            {paymentsExpiredData?.message &&
              paymentsExpiredData.expired_payments_count > 0 && (
                <Alert type="error" icon={<CreditCard />}>
                  <span>{paymentsExpiredData.message}</span>
                  <Link to="/dashboard/payments" className={styles.link}>
                    <Trans>Przejdź do zakładki</Trans>{" "}
                    <ArrowIcon className={styles.nextIcon} />
                  </Link>
                </Alert>
              )}

            <ActionBar cart={currentCart} setSearchKeyword={setSearchQuery} />
            <CartPositions
              cartId={cartId}
              variant={isMobile ? "small" : undefined}
              onChange={() => {
                refetchCartsData();
                refetchCheckoutAgreementData();
              }}
              searchKeyword={searchQuery}
              noDataPlaceholder={
                <div className={styles.notFound}>
                  <h3>
                    <Trans>Brak wyników</Trans>
                  </h3>
                  {searchQuery && (
                    <span>
                      <Trans>Zmień kryteria wyszukiwania</Trans>
                    </span>
                  )}
                </div>
              }
            />

            {/* Sekcja zgód - przeniesiona z checkout */}
            {checkoutAgreementData?.items.length > 0 && (
              <div className={styles.collapse}>
                <div
                  className={classnames({
                    [styles.header]: true,
                    [styles.open]: expandedSections.includes("contracts"),
                  })}
                  onClick={() => toggleSections("contracts")}
                >
                  <span>{t("Zgody")}</span> <ChevronDown />
                </div>
                {expandedSections.includes("contracts") && (
                  <div className={styles.content}>
                    <Contracts
                      cartId={cartId}
                      contracts={checkoutAgreementData?.items || []}
                      selectedContractIds={selectedContractIds}
                      setSelectedContractIds={setSelectedContractIds}
                      refetchCheckoutAgreementData={
                        refetchCheckoutAgreementData
                      }
                      afterContractChange={() => {
                        refetchCartData();
                        refetchCartsData();
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.sidebar}>
            <CartSummary
              cartId={cartId}
              buttonOnClick={handleProceedToCheckout}
              buttonLabel={t("Przejdź do danych adresowych")}
            />

            <div className={styles.collapse}>
              <div
                className={classnames({
                  [styles.header]: true,
                  [styles.open]: expandedSections.includes("comment"),
                })}
                onClick={() => toggleSections("comment")}
              >
                <span>
                  <Trans>Komentarz do zamówienia</Trans>
                </span>{" "}
                <ChevronDown />
              </div>
              <div
                className={classnames({
                  [styles.content]: true,
                  [styles.hidden]: !expandedSections.includes("comment"),
                })}
              >
                <TextareaAutosize
                  className={styles.textarea}
                  value={comment}
                  placeholder={`${t("napisz komentarz")}...`}
                  minRows={3}
                  onChange={(e) => setComment(e.target.value)}
                  onBlur={() => {
                    if (comment !== (cartMainData?.comment ?? "")) {
                      putCartMainData({ comment });
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Cart;
